
import * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const avatarVariants = cva('relative flex shrink-0 overflow-hidden', {
  variants: {
    size: {
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
      xl: 'h-16 w-16 text-lg',
    },
    shape: {
      circle: 'rounded-full',
      square: 'rounded-md',
    },
  },
  defaultVariants: {
    size: 'md',
    shape: 'square',
  },
})

const avatarImageVariants = cva('aspect-square h-full w-full', {
  variants: {
    shape: {
      circle: 'rounded-full',
      square: 'rounded-md',
    },
  },
  defaultVariants: {
    shape: 'square',
  },
})

const avatarFallbackVariants = cva(
  'flex h-full w-full items-center justify-center bg-muted text-muted-foreground',
  {
    variants: {
      shape: {
        circle: 'rounded-full',
        square: 'rounded-md',
      },
    },
    defaultVariants: {
      shape: 'square',
    },
  }
)

// Base components for advanced usage
interface AvatarRootProps
  extends React.ComponentProps<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

const AvatarRoot = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarRootProps
>(({ className, size, shape, ...props }, ref) => {
  const avatarSize = size || 'md'
  const avatarShape = shape || 'square'
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        avatarVariants({ size: avatarSize, shape: avatarShape }),
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
AvatarRoot.displayName = 'AvatarRoot'

interface AvatarImageProps
  extends React.ComponentProps<typeof AvatarPrimitive.Image>,
    VariantProps<typeof avatarImageVariants> {}

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ className, shape, ...props }, ref) => {
  const imageShape = shape || 'square'
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(avatarImageVariants({ shape: imageShape }), className)}
      ref={ref}
      {...props}
    />
  )
})
AvatarImage.displayName = 'AvatarImage'

interface AvatarFallbackProps
  extends React.ComponentProps<typeof AvatarPrimitive.Fallback>,
    VariantProps<typeof avatarFallbackVariants> {}

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, shape, ...props }, ref) => {
  const fallbackShape = shape || 'square'
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        avatarFallbackVariants({ shape: fallbackShape }),
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
AvatarFallback.displayName = 'AvatarFallback'

// Simple wrapper component for common usage
interface AvatarProps {
  src?: string
  alt?: string
  fallback?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  shape?: 'square' | 'circle'
  className?: string
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  (
    { src, alt, fallback, size = 'md', shape = 'square', className, ...props },
    ref
  ) => (
    <AvatarRoot
      ref={ref}
      size={size}
      shape={shape}
      className={className}
      {...props}
    >
      <AvatarImage src={src} alt={alt} shape={shape} />
      <AvatarFallback shape={shape}>{fallback}</AvatarFallback>
    </AvatarRoot>
  )
)

Avatar.displayName = 'Avatar'

export { Avatar, AvatarRoot, AvatarImage, AvatarFallback, avatarVariants }